import { planErrorMessage } from '@/lib/plan-errors';

const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: { field: string; message: string }[],
    /** Raw error envelope, for structured 409s (e.g. stock `shortfalls`). */
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The envelope's string `code` (e.g. `PLAN_LIMIT_REACHED`), when the API sent one. */
  get code(): string | undefined {
    return typeof this.body?.code === 'string' ? this.body.code : undefined;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

/**
 * Shared transport for every helper below: one fetch, one 401→refresh→retry,
 * one error mapping. Returns the parsed JSON envelope `{ statusCode, data, meta?, … }`.
 */
async function request(path: string, options: RequestOptions = {}): Promise<Record<string, unknown>> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };
  const init: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  };

  let res = await fetch(`${API_BASE}${path}`, init);

  // On 401, try refreshing the token once
  if (res.status === 401 && !skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, init);
    } else {
      localStorage.removeItem('bbpos_user');
      window.location.href = '/login';
      throw new ApiError(401, 'Session expired. Please log in again.');
    }
  }

  const json = await res.json().catch(() => null);

  if (!res.ok) {
    const message =
      json?.message ??
      json?.error ??
      `Request failed with status ${res.status}`;
    const errorMessage =
      planErrorMessage(json) ?? (Array.isArray(message) ? message.join(', ') : message);
    throw new ApiError(res.status, errorMessage, json?.details, json ?? undefined);
  }

  return (json ?? {}) as Record<string, unknown>;
}

/** Most endpoints: returns the envelope's `data` only. */
export async function apiClient<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const json = await request(path, options);
  return json.data as T;
}

/**
 * Fetch a paginated list endpoint, returning both `data` and `meta`.
 * Use this for list APIs where you need pagination metadata (total, page, etc.).
 */
export async function apiClientPaginated<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: { page: number; limit: number; total: number; totalPages: number } }> {
  const json = await request(path, options);
  return { data: json.data as T, meta: json.meta as { page: number; limit: number; total: number; totalPages: number } };
}

/**
 * Fetch an endpoint whose body carries more than `data` + `meta` (e.g. a
 * report's `summary`). Returns the whole envelope minus `statusCode`. Using
 * `apiClient` for these strips everything but `data` — the inventory valuation
 * KPIs showed ₹0 for exactly that reason.
 */
export async function apiClientEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { statusCode: _statusCode, ...rest } = await request(path, options);
  return rest as T;
}

/**
 * Fetch a binary/stream endpoint and return the raw Blob plus the filename
 * advertised by Content-Disposition. Used for invoice downloads and report
 * exports that do not pass through the JSON envelope.
 */
export async function apiBlob(
  path: string,
  options: Omit<RequestOptions, 'body'> = {},
): Promise<{ blob: Blob; filename?: string }> {
  const { method = 'GET', headers = {} } = options;

  let res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, { method, headers, credentials: 'include' });
    } else {
      localStorage.removeItem('bbpos_user');
      window.location.href = '/login';
      throw new ApiError(401, 'Session expired. Please log in again.');
    }
  }

  if (!res.ok) {
    const json = await res.json().catch(() => null);
    const message = json?.message ?? json?.error ?? `Download failed with status ${res.status}`;
    const errorMessage =
      planErrorMessage(json) ?? (Array.isArray(message) ? message.join(', ') : message);
    throw new ApiError(res.status, errorMessage, json?.details, json ?? undefined);
  }

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] };
}
